// Removed duplicate and misplaced @Input() declarations
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../atoms/button/button.component';

export interface WizardStep {
  title: string;
  description?: string;
  icon?: string;
  canEnter: boolean;
  canExit: boolean;
  completed: boolean;
}

@Component({
  selector: 'app-wizard',
  standalone: true,
  imports: [CommonModule, ButtonComponent],
  templateUrl: './wizard.component.html',
  styleUrls: ['./wizard.component.scss'],
})
export class WizardComponent implements OnInit {
  @Input() hideFinishButton: boolean = false;
  // Removed duplicate showNextButton property
  @Input() steps: WizardStep[] = [];
  @Input() currentStep: number = 0;
  @Input() showProgressBar: boolean = true;
  @Input() showStepNumbers: boolean = true;
  @Input() allowStepClick: boolean = true;
  @Input() showPreviousButton: boolean = true;
  @Input() showNextButton: boolean = true;
  @Input() showResetButton: boolean = false;
  @Input() previousButtonText: string = 'Previous';
  @Input() nextButtonText: string = 'Next';
  @Input() resetButtonText: string = 'Reset';
  @Input() finishButtonText: string = 'Complete';

  @Output() currentStepChange = new EventEmitter<number>(); // For two-way binding
  @Output() stepChanged = new EventEmitter<number>();
  @Output() nextClicked = new EventEmitter<number>();
  @Output() previousClicked = new EventEmitter<number>();
  @Output() resetClicked = new EventEmitter<void>();
  @Output() finishClicked = new EventEmitter<void>();
  @Output() stepClicked = new EventEmitter<number>();

  ngOnInit() {
    // Initialize first step if not already set
    if (this.steps.length > 0 && !this.steps[0].canEnter) {
      this.steps[0].canEnter = true;
    }
  }

  canNavigateToStep(index: number): boolean {
    return this.allowStepClick && this.steps[index]?.canEnter;
  }

  goToStep(index: number) {
    if (this.canNavigateToStep(index)) {
      this.currentStep = index;
      this.currentStepChange.emit(index); // Emit for two-way binding
      this.stepChanged.emit(index);
      this.stepClicked.emit(index);
    }
  }

  nextStep() {
    // In edit mode, allow advancing regardless of canExit
    if (this.isEditMode) {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
        this.currentStepChange.emit(this.currentStep);
        this.stepChanged.emit(this.currentStep);
        this.nextClicked.emit(this.currentStep);
      }
      return;
    }
    if (this.currentStep < this.steps.length - 1 && this.steps[this.currentStep]?.canExit) {
      this.currentStep++;
      this.currentStepChange.emit(this.currentStep); // Emit for two-way binding
      this.stepChanged.emit(this.currentStep);
      this.nextClicked.emit(this.currentStep);
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.currentStepChange.emit(this.currentStep); // Emit for two-way binding
      this.stepChanged.emit(this.currentStep);
      this.previousClicked.emit(this.currentStep);
    }
  }

  resetWizard() {
    this.currentStep = 0;
    this.currentStepChange.emit(0); // Emit for two-way binding
    this.steps.forEach((step, index) => {
      step.completed = false;
      step.canEnter = index === 0;
      step.canExit = false;
    });
    this.resetClicked.emit();
  }

  finish() {
    this.finishClicked.emit();
  }

  isStepActive(index: number): boolean {
    return this.currentStep === index;
  }

  isStepCompleted(index: number): boolean {
    return this.steps[index]?.completed || false;
  }

  get progressPercentage(): number {
    if (this.steps.length === 0) return 0;
    return ((this.currentStep + 1) / this.steps.length) * 100;
  }

  get isFirstStep(): boolean {
    return this.currentStep === 0;
  }

  get isLastStep(): boolean {
    return this.currentStep === this.steps.length - 1;
  }

  @Input() isEditMode: boolean = false;
  get canGoNext(): boolean {
    // In edit mode, always enable Next button
    if (this.isEditMode) return !this.isLastStep;
    return !this.isLastStep && this.steps[this.currentStep]?.canExit;
  }

  get canGoPrevious(): boolean {
    return !this.isFirstStep;
  }

  /**
   * Calculate GREEN progress line width for completed steps only
   */
  getCompletedProgressLineWidth(): number {
    // Find the highest completed step index
    let highestCompletedIndex = -1;
    for (let i = 0; i < this.steps.length; i++) {
      if (this.steps[i]?.completed) {
        highestCompletedIndex = i;
      }
    }

    // Return 0 if no steps completed
    if (highestCompletedIndex === -1) return 0;
    
    // Each step gap is 8.5rem (6rem gap + 2.5rem for step circle)
    return highestCompletedIndex * 8.5;
  }

  /**
   * Calculate BLUE progress line starting position (after green line)
   */
  getBlueLineStart(): string {
    // Find the highest completed step index
    let highestCompletedIndex = -1;
    for (let i = 0; i < this.steps.length; i++) {
      if (this.steps[i]?.completed) {
        highestCompletedIndex = i;
      }
    }

    // Calculate starting position
    // If no completed steps, start from center (50% - half of total width)
    // If there are completed steps, start after the last completed step
    const totalWidth = (this.steps.length - 1) * 8.5;
    const startOffset = totalWidth / 2; // Half of total width for centering
    
    if (highestCompletedIndex === -1) {
      return `calc(50% - ${startOffset}rem)`;
    }
    
    const completedWidth = highestCompletedIndex * 8.5;
    return `calc(50% - ${startOffset}rem + ${completedWidth}rem)`;
  }

  /**
   * Calculate BLUE progress line width (from last completed to current)
   */
  getBlueLineWidth(): number {
    // Find the highest completed step index
    let highestCompletedIndex = -1;
    for (let i = 0; i < this.steps.length; i++) {
      if (this.steps[i]?.completed) {
        highestCompletedIndex = i;
      }
    }

    // If current step is before or equal to last completed, no blue line
    if (this.currentStep <= highestCompletedIndex) {
      return 0;
    }

    // Blue line width is the gap from last completed step to current step
    // Number of gaps = currentStep - highestCompletedIndex
    const gapCount = this.currentStep - highestCompletedIndex;
    
    return gapCount * 8.5;
  }
}
